/**
 * Interface sprites (spec/props-pipeline.md P12). Every icon the interface shows is a
 * `kind: "ui"` entry in `props/props.json`, drawn by the props pipeline into
 * `src/assets/ui/` as one strip of frames, and played here by CSS `steps()` — never an
 * emoji, never a glyph font, never a hand-placed image. Motion stops under
 * `prefers-reduced-motion` (frame 0 stays), the rule every animation in the lab follows
 * (P4, P11). The `lab-asset` skill is the way to add one.
 */
import manifest from '../../props/props.json';
import type { PropAnimation } from '../world/props.js';

export interface UiSpriteEntry {
  id: string;
  kind: 'ui';
  source: string;
  animation?: PropAnimation;
  /** The square the strip was drawn at; default the manifest's `ui.size`. */
  size?: number;
}

/** Bundled strips by id (`<id>.sheet.png` when animated, `<id>.png` when still). */
const URLS: Record<string, string> = {};
for (const [path, url] of Object.entries(
  import.meta.glob('../assets/ui/*.png', { eager: true, query: '?url', import: 'default' }),
)) {
  const file = path.slice(path.lastIndexOf('/') + 1, -'.png'.length);
  URLS[file.endsWith('.sheet') ? file.slice(0, -'.sheet'.length) : file] = url as string;
}

const ENTRIES = (manifest.props as Array<{ id: string; kind?: string }>).filter(
  (p): p is UiSpriteEntry => p.kind === 'ui',
);

/** The manifest entry of an interface sprite, or null for anything else. */
export function uiSpriteEntry(id: string): UiSpriteEntry | null {
  return ENTRIES.find((p) => p.id === id) ?? null;
}

/** Every interface sprite the manifest declares. */
export function uiSpriteIds(): string[] {
  return ENTRIES.map((p) => p.id);
}

/** The bundled strip of an interface sprite, or null when the pipeline has not drawn it. */
export function uiSpriteUrl(id: string): string | null {
  return uiSpriteEntry(id) ? (URLS[id] ?? null) : null;
}

/** The square a sprite was drawn at, in source pixels. */
export function uiSpriteSize(id: string): number {
  return uiSpriteEntry(id)?.size ?? (manifest as { ui?: { size?: number } }).ui?.size ?? 64;
}

export interface UiSpriteOptions {
  doc?: Document;
  /** Rendered size in CSS px. Default 24. */
  size?: number;
  /** A name for assistive tech; without one the sprite is decoration. */
  label?: string;
  /**
   * Stand on this frame and play nothing: for a strip whose frames are counts rather
   * than a loop (`ui-gauge`, the Treasury's coins, the garden's sprouts — P10, Q11).
   * The caller steps it with `setUiSpriteFrame`.
   */
  frame?: number;
  /** The viewer's device pixel ratio, for the Q4 test. Default the window's. */
  dpr?: number;
}

/**
 * Q4, for the chrome: a strip drawn at `source` px into a `box` CSS px square is
 * resampled by an integer factor — and so may be drawn nearest-neighbour — only when the
 * device pixel ratio is a whole number and the two sizes divide one another exactly. At
 * any other ratio nearest drops rows and is worse than the blur, so the box is smoothed.
 *
 * Measured on this app (2026-09-20): the boxes in use are 14, 16, 18, 20, 22, 24, 26,
 * 28, 30, 40, 48 and 80 CSS px against a 64 px strip, and **only 16** is exact — at dpr 1
 * (64 = 4 x 16) and at dpr 2 (64 = 2 x 32) alike. Everything else is smoothed on purpose.
 * A new box picked from 8 / 16 / 32 / 64 lands exact at both ratios; the HUD's do.
 */
export function uiSpriteExact(source: number, box: number, dpr = 1): boolean {
  if (!(source > 0) || !(box > 0) || !(dpr > 0)) return false;
  if (!Number.isInteger(dpr)) return false;
  const device = box * dpr;
  if (!Number.isInteger(device)) return false;
  return source % device === 0 || device % source === 0;
}

/**
 * Put a sprite on one frame of its strip, with nothing playing it — the CSS half of
 * `PropAnimator.count` (Q11): a count changes by showing the frame that count is drawn
 * at, never by a width, a scale or a tween. Out-of-range indices clamp.
 */
export function setUiSpriteFrame(el: HTMLElement, index: number): void {
  const frames = Math.max(1, Number(el.dataset.frames ?? 1));
  const i = Math.max(0, Math.min(frames - 1, Math.floor(index)));
  el.style.animationName = 'none';
  el.style.backgroundPositionX = `calc(var(--size) * ${-i})`;
  el.dataset.frame = String(i);
}

/**
 * The sprite as an element: a square whose background is the strip, stepped one frame at
 * a time by CSS. The frame count and duration come from the manifest, so a redraw with
 * more frames needs no code change. An id the manifest does not know renders as an empty
 * square marked `ui-sprite--missing`, so a test can catch it and the page never throws.
 */
export function uiSprite(id: string, opts: UiSpriteOptions = {}): HTMLElement {
  const doc = opts.doc ?? document;
  const el = doc.createElement('span');
  el.className = 'ui-sprite';
  el.dataset.sprite = id;
  if (opts.label) {
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', opts.label);
  } else el.setAttribute('aria-hidden', 'true');
  const entry = uiSpriteEntry(id);
  const url = uiSpriteUrl(id);
  if (!entry || !url) {
    el.classList.add('ui-sprite--missing');
    return el;
  }
  const frames = Math.max(1, Math.floor(entry.animation?.frames ?? 1));
  const fps = entry.animation?.fps ?? 6;
  const size = opts.size ?? 24;
  el.style.setProperty('--sheet', `url("${url}")`);
  el.style.setProperty('--frames', String(frames));
  el.style.setProperty('--size', `${size}px`);
  el.dataset.frames = String(frames);
  // Q4: nearest neighbour where the ratio is an integer, and only there. The world layer
  // chooses its filter per draw (`artExact`); the chrome cannot, so the one place that
  // knows both the strip's pixels and the box's says it once, here.
  const dpr =
    opts.dpr ??
    doc.defaultView?.devicePixelRatio ??
    (globalThis as { devicePixelRatio?: number }).devicePixelRatio ??
    1;
  if (uiSpriteExact(uiSpriteSize(id), size, dpr)) el.classList.add('ui-sprite--exact');
  if (opts.frame !== undefined) {
    setUiSpriteFrame(el, opts.frame);
    return el;
  }
  if (frames > 1) {
    el.style.animationDuration = `${frames / fps}s`;
    el.style.animationTimingFunction = `steps(${frames})`;
    if (entry.animation?.loop === false) el.style.animationIterationCount = '1';
  } else {
    el.style.animationName = 'none';
  }
  return el;
}

/**
 * A module's icon (`ModuleMeta.icon`) as an element. Every built-in module's icon is an
 * interface sprite id; a manifest that still names a glyph shows it as text rather than
 * nothing, until it is redrawn (props-pipeline.md, "not true").
 */
export function moduleIcon(icon: string, opts: UiSpriteOptions = {}): HTMLElement {
  if (uiSpriteEntry(icon)) return uiSprite(icon, opts);
  const doc = opts.doc ?? document;
  const el = doc.createElement('span');
  el.className = 'module-glyph';
  el.setAttribute('aria-hidden', 'true');
  el.textContent = icon;
  return el;
}
